/**
 * Distribution screen — Dead letters tab (task 005-08, ADR-010 §4 / ADR-001's
 * "unfinished operations surfaced in the admin").
 *
 * The graphile jobs that exhausted their attempt budget (`attempts >=
 * max_attempts` with a `last_error`), most recently failed first. One write
 * action: **Retry** re-queues a single job (attempt budget reset, error
 * cleared, run now) — no bulk retry, no discard in R1. The job / lead /
 * campaign handles are the identifiers ops quotes in support conversations
 * (graphile job id, lead id, campaign id) — the attempts tab is the surface
 * that hides DB ids; this queue has no readable row to point at.
 */

import { useCallback, useEffect, useState } from 'react';
import { type DlqJobResponse } from '@leads-platform/shared';
import { ApiError } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/toast';
import { WhenCell } from './when';
import { getDistributionDlq, retryDistributionJob } from './api';

/** First line of the stored error — the rest sits in the cell's tooltip. */
function firstLine(text: string): string {
  const newline = text.indexOf('\n');
  return newline === -1 ? text : text.slice(0, newline);
}

/** `Attempts 8 / 8` — the budget the job burned through. */
function attemptsLabel(job: DlqJobResponse): string {
  return `${job.attempts} / ${job.maxAttempts}`;
}

export function DlqTab() {
  const [jobs, setJobs] = useState<DlqJobResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The job whose retry is in flight — one spinner at a time, others stay
  // clickable.
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    getDistributionDlq()
      .then(setJobs)
      .catch((err: ApiError) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const retry = (job: DlqJobResponse) => {
    setRetryingJobId(job.id);
    retryDistributionJob(job.id)
      .then(() => {
        toast.success('Job re-queued');
        setJobs((prev) => prev.filter((entry) => entry.id !== job.id));
      })
      .catch((err: ApiError) => toast.error(`Retry failed: ${err.message}`))
      .finally(() => setRetryingJobId(null));
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[13px] text-muted">
          Jobs that exhausted their attempts. Retry resets the attempt budget and runs the job now.
        </p>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                <th className="px-4 py-2.5 font-semibold">Job</th>
                <th className="px-4 py-2.5 font-semibold">Lead</th>
                <th className="px-4 py-2.5 font-semibold">Campaign</th>
                <th className="px-4 py-2.5 font-semibold">Attempts / max</th>
                <th className="px-4 py-2.5 font-semibold">Last error</th>
                <th className="px-4 py-2.5 font-semibold">Last run</th>
                <th className="px-4 py-2.5 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-faint">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-destructive">
                    Error loading dead letters: {error}
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-faint">
                    The dead-letter queue is empty.
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id} className="border-b border-line/70 transition hover:bg-paper/60">
                    <td className="px-4 py-3 font-mono text-xs">{job.id}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">
                      {job.leadId ?? <span className="text-faint">--</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">
                      {job.campaignId ?? <span className="text-faint">--</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted">{attemptsLabel(job)}</td>
                    <td
                      className="max-w-[280px] truncate px-4 py-3 text-muted"
                      title={job.lastError ?? undefined}
                    >
                      {job.lastError ? (
                        firstLine(job.lastError)
                      ) : (
                        <span className="text-faint">--</span>
                      )}
                    </td>
                    <WhenCell iso={job.runAt} />
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => retry(job)}
                        disabled={retryingJobId === job.id}
                      >
                        {retryingJobId === job.id ? 'Retrying…' : 'Retry'}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-3 font-mono text-xs text-faint">
        {jobs.length} dead {jobs.length === 1 ? 'job' : 'jobs'} · most recently failed first
      </p>
    </div>
  );
}
